import React, { memo } from 'react'
import tw from 'twin.macro'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Typography } from '@mui/material'
import {
  DECLINED_STATUS_LABEL,
  METADATA_LABEL_COLOR,
  statusLabels,
  masterContractorStatusLabels,
} from '../constants'
import { ContractorStepDetailsDeclineReasonBlock } from './ContractorStepDetailsDeclineReasonBlock'
import { ContractorStepDetailsStatusLabel } from './ContractorStepDetailsStatusLabel'
import { ContractorStepDetailsCardActions } from './ContractorStepDetailsCardActions'
import { ContractorStepDetailsCardUsageVariant } from '../types'
import type { SubmissionBlock } from './ContractorStepDetailsCardContent'

dayjs.extend(utc)

interface ContractorStepDetailsBaseContentBlockProps {
  block: SubmissionBlock
  usageVariant: ContractorStepDetailsCardUsageVariant
}

const ContractorStepDetailsBaseContentBlockComponent = ({
  block,
  usageVariant,
}: ContractorStepDetailsBaseContentBlockProps) => {
  const showReviewButtonWithText = block.showReviewButtonWithText ?? false

  const baseStatusLabel =
    usageVariant === ContractorStepDetailsCardUsageVariant.MasterContractor
      ? masterContractorStatusLabels[block.status]
      : statusLabels[block.status]
  const declineReason = block.collectedData?.lastRejectionReason
  const statusLabel = declineReason ? DECLINED_STATUS_LABEL : baseStatusLabel
  const effectiveAtFormatted =
    block.effectiveAt ??
    (block.collectedData?.effectiveAt
      ? dayjs(block.collectedData.effectiveAt).utc().format('MM/DD/YYYY')
      : undefined)

  return (
    <div css={tw`px-4`}>
      <div css={[tw`flex flex-row items-start gap-x-6 min-w-0 w-full py-4`]}>
        <div css={tw`flex flex-col min-w-0 shrink`}>
          <Typography variant="body2" fontWeight={400} color={METADATA_LABEL_COLOR}>
            Submission
          </Typography>
          {statusLabel != null && (
            <ContractorStepDetailsStatusLabel
              label={statusLabel}
              status={block.status}
              isDeclined={!!declineReason}
            />
          )}
        </div>
        {(block.expiresAt || effectiveAtFormatted) && (
          <div css={tw`flex flex-col gap-0 min-w-0 shrink items-start`}>
            {block.expiresAt && (
              <div css={tw`flex flex-row items-start gap-x-2`}>
                <Typography variant="body2" color={METADATA_LABEL_COLOR}>
                  Expiration
                </Typography>
                <Typography variant="body2" color="textPrimary">
                  {block.expiresAt}
                </Typography>
              </div>
            )}
            {effectiveAtFormatted && (
              <div css={tw`flex flex-row items-start gap-x-2`}>
                <Typography variant="body2" color={METADATA_LABEL_COLOR}>
                  Effective
                </Typography>
                <Typography variant="body2" color="textPrimary">
                  {effectiveAtFormatted}
                </Typography>
              </div>
            )}
          </div>
        )}
        <div css={tw`flex-1 min-w-0 shrink`} />
        <div css={tw`ml-2 shrink-0 flex items-center`}>
          <ContractorStepDetailsCardActions
            actions={block.actions}
            onReviewStepDataAction={block.onReviewStepDataAction}
            showReviewButtonWithText={showReviewButtonWithText}
          />
        </div>
      </div>
      {declineReason && (
        <div css={tw`-mx-4 mb-0`}>
          <ContractorStepDetailsDeclineReasonBlock declineReason={declineReason} />
        </div>
      )}
    </div>
  )
}

export const ContractorStepDetailsBaseContentBlock = memo(
  ContractorStepDetailsBaseContentBlockComponent,
)
